import React, { useState, useEffect } from 'react';
import { trackingService } from '../../../services/trackingService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { format } from 'date-fns';

const AdminAnalyticsDashboard = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('errors');
  const [errorLogs, setErrorLogs] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [formAnalytics, setFormAnalytics] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [formAbandonmentSummary, setFormAbandonmentSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorFilter, setErrorFilter] = useState('unresolved');
  const [formFilter, setFormFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, [activeTab, errorFilter, formFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'errors') {
        const response = await trackingService?.getErrorLogs({
          resolved: errorFilter === 'resolved' ? true : errorFilter === 'unresolved' ? false : undefined,
          limit: 50
        });
        setErrorLogs(response?.data || []);
      } else if (activeTab === 'activity') {
        const response = await trackingService?.getUserActivity({ limit: 100 });
        setUserActivity(response?.data || []);
      } else if (activeTab === 'forms') {
        const [analyticsResponse, summaryResponse] = await Promise.all([
          trackingService?.getFormAnalytics({
            formName: formFilter !== 'all' ? formFilter : undefined,
            limit: 100
          }),
          trackingService?.getFormAbandonmentSummary()
        ]);
        setFormAnalytics(analyticsResponse?.data || []);
        setFormAbandonmentSummary(summaryResponse?.data || []);
      } else if (activeTab === 'performance') {
        const response = await trackingService?.getPerformanceMetrics({ limit: 100 });
        setPerformanceMetrics(response?.data || []);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkResolved = async (errorId) => {
    try {
      await trackingService?.markErrorResolved(errorId, null);
      fetchData();
    } catch (error) {
      console.error('Error marking as resolved:', error);
    }
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      form_start: 'bg-blue-100 text-blue-800',
      field_change: 'bg-gray-100 text-gray-800',
      field_blur: 'bg-gray-100 text-gray-600',
      validation_error: 'bg-red-100 text-red-800',
      form_submit: 'bg-green-100 text-green-800',
      form_abandon: 'bg-orange-100 text-orange-800'
    };
    return colors?.[eventType] || 'bg-gray-100 text-gray-800';
  };

  const getMetricTypeColor = (metricType) => {
    const colors = {
      page_load: 'bg-blue-100 text-blue-800',
      api_call: 'bg-purple-100 text-purple-800',
      resource_load: 'bg-cyan-100 text-cyan-800',
      form_submission: 'bg-green-100 text-green-800',
      image_upload: 'bg-yellow-100 text-yellow-800'
    };
    return colors?.[metricType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Analytics Dashboard</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track errors, user activity, form analytics, and performance metrics
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6">
          {[
            { id: 'errors', label: 'Error Logs', icon: 'AlertCircle' },
            { id: 'activity', label: 'User Activity', icon: 'Activity' },
            { id: 'forms', label: 'Form Analytics', icon: 'FileText' },
            { id: 'performance', label: 'Performance', icon: 'Zap' }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} className="w-4 h-4" />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Error Logs Tab */}
              {activeTab === 'errors' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Error Logs</h3>
                    <Select
                      value={errorFilter}
                      onChange={(e) => setErrorFilter(e?.target?.value)}
                      className="w-48"
                    >
                      <option value="all">All Errors</option>
                      <option value="unresolved">Unresolved</option>
                      <option value="resolved">Resolved</option>
                    </Select>
                  </div>

                  {errorLogs?.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No error logs found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {errorLogs?.map((error) => (
                        <div
                          key={error?.id}
                          className="bg-background border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                                  {error?.errorType}
                                </span>
                                {error?.resolved && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                                    Resolved
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(error?.createdAt), 'MMM dd, yyyy HH:mm')}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-foreground mb-1">
                                {error?.errorMessage}
                              </p>
                              <p className="text-xs text-muted-foreground mb-2">
                                {error?.pageUrl}
                              </p>
                              {error?.errorStack && (
                                <details className="text-xs text-muted-foreground">
                                  <summary className="cursor-pointer hover:text-foreground">
                                    View Stack Trace
                                  </summary>
                                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                                    {error?.errorStack}
                                  </pre>
                                </details>
                              )}
                            </div>
                            {!error?.resolved && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkResolved(error?.id)}
                              >
                                Mark Resolved
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* User Activity Tab */}
              {activeTab === 'activity' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">User Activity Timeline</h3>
                  {userActivity?.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Icon name="Activity" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No user activity recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {userActivity?.map((activity) => (
                        <div
                          key={activity?.id}
                          className="bg-background border border-border rounded-lg p-3 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  {activity?.activityType}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(activity?.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                                </span>
                              </div>
                              <p className="text-sm text-foreground">{activity?.activityDescription}</p>
                              <p className="text-xs text-muted-foreground mt-1">{activity?.pageUrl}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Form Analytics Tab */}
              {activeTab === 'forms' && (
                <div className="space-y-6">
                  {/* Form Abandonment Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Form Abandonment Summary</h3>
                    {formAbandonmentSummary?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No form data available</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {formAbandonmentSummary?.map((summary) => (
                          <div
                            key={summary?.id}
                            className="bg-background border border-border rounded-lg p-4"
                          >
                            <h4 className="font-medium text-foreground mb-3">{summary?.formName}</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Total Starts:</span>
                                <span className="font-medium text-foreground">{summary?.totalStarts}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Completions:</span>
                                <span className="font-medium text-green-600">{summary?.totalCompletions}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Abandonments:</span>
                                <span className="font-medium text-red-600">{summary?.totalAbandonments}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-border">
                                <span className="text-muted-foreground">Completion Rate:</span>
                                <span className="font-bold text-primary">{summary?.completionRate}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Abandonment Rate:</span>
                                <span className="font-bold text-orange-600">{summary?.abandonmentRate}%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Form Events */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-foreground">Form Events</h3>
                      <Select
                        value={formFilter}
                        onChange={(e) => setFormFilter(e?.target?.value)}
                        className="w-64"
                      >
                        <option value="all">All Forms</option>
                        <option value="player-registration-form">Player Registration</option>
                      </Select>
                    </div>

                    {formAnalytics?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No form events recorded</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {formAnalytics?.map((event) => (
                          <div
                            key={event?.id}
                            className="bg-background border border-border rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${getEventTypeColor(event?.eventType)}`}>
                                    {event?.eventType?.replace('_', ' ')}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(event?.createdAt), 'MMM dd, HH:mm:ss')}
                                  </span>
                                  {event?.timeSpentSeconds > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      ({event?.timeSpentSeconds}s)
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-foreground">
                                  {event?.formName}
                                  {event?.fieldName && ` - ${event?.fieldName}`}
                                </p>
                                {event?.errorMessage && (
                                  <p className="text-xs text-red-600 mt-1">{event?.errorMessage}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Performance Tab */}
              {activeTab === 'performance' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Performance Metrics</h3>
                  {performanceMetrics?.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Icon name="Zap" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No performance metrics recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {performanceMetrics?.map((metric) => (
                        <div
                          key={metric?.id}
                          className="bg-background border border-border rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-1 text-xs font-medium rounded ${getMetricTypeColor(metric?.metricType)}`}>
                                  {metric?.metricType?.replace('_', ' ')}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(metric?.createdAt), 'MMM dd, HH:mm:ss')}
                                </span>
                                <span className={`text-xs font-bold ${
                                  metric?.success ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {metric?.success ? '✓ Success' : '✗ Failed'}
                                </span>
                              </div>
                              <div className="flex items-center gap-4">
                                <p className="text-sm text-foreground">{metric?.metricName}</p>
                                <span className="text-sm font-bold text-primary">
                                  {metric?.durationMs}ms
                                </span>
                              </div>
                              {metric?.errorMessage && (
                                <p className="text-xs text-red-600 mt-1">{metric?.errorMessage}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;